/* =============================================================
   Authentic Afghan Saffron — luxe-ui.js
   AJAX cart drawer + predictive search overlay + conversion
   micro-interactions. Pure progressive enhancement: with JS off,
   cart forms post natively to /cart and the search icon links to
   /search. No frameworks; compositor-only animation hooks.
   ============================================================= */
(function () {
  'use strict';

  /* ---------------- shared helpers ---------------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function resizeImg(url, size) {
    try {
      var u = new URL(url, window.location.origin);
      u.pathname = u.pathname.replace(/(\.[^.\/]+)$/, '_' + size + 'x$1');
      return u.toString();
    } catch (e) { return url; }
  }
  function decodeEntities(str) {
    var t = document.createElement('textarea');
    t.innerHTML = String(str == null ? '' : str);
    return t.value;
  }
  var drawerEl = document.getElementById('CartDrawer');
  var CUR = (drawerEl && drawerEl.getAttribute('data-currency')) || 'USD';
  function money(cents) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: CUR }).format(cents / 100);
    } catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function lockScroll(on) { document.documentElement.classList.toggle('luxe-lock', !!on); }

  function updateBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count > 0 ? count : '';
      el.classList.remove('is-bump');
      void el.offsetWidth;
      if (count > 0) el.classList.add('is-bump');
    });
  }

  /* =================================================
     CART DRAWER
     ================================================= */
  var cdrawer = (function () {
    if (!drawerEl) return null;
    var panel = drawerEl.querySelector('.cdrawer__panel');
    var itemsEl = drawerEl.querySelector('[data-cdrawer-items]');
    var emptyEl = drawerEl.querySelector('[data-cdrawer-empty]');
    var footEl = drawerEl.querySelector('[data-cdrawer-foot]');
    var subEl = drawerEl.querySelector('[data-cdrawer-subtotal]');
    var countEl = drawerEl.querySelector('[data-cdrawer-count]');
    var meterEl = drawerEl.querySelector('[data-cdrawer-meter]');
    var meterMsg = drawerEl.querySelector('[data-cdrawer-meter-msg]');
    var meterFill = drawerEl.querySelector('[data-cdrawer-meter-fill]');
    var statusEl = drawerEl.querySelector('[data-cdrawer-status]');
    var THRESHOLD = parseInt(drawerEl.getAttribute('data-threshold'), 10) || 3900;
    var MSG_PROGRESS = decodeEntities(drawerEl.getAttribute('data-msg-progress') || "You're [amount] away from free shipping.");
    var MSG_UNLOCKED = decodeEntities(drawerEl.getAttribute('data-msg-unlocked') || 'Free shipping unlocked.');
    var lastFocus = null;
    var isOpen = false;

    function render(cart) {
      var has = cart.item_count > 0;
      emptyEl.hidden = has;
      footEl.hidden = !has;
      meterEl.hidden = !has;
      countEl.textContent = has ? '· ' + cart.item_count : '';
      subEl.textContent = money(cart.items_subtotal_price);

      if (has) {
        var pct = Math.min(100, Math.round(cart.items_subtotal_price * 100 / THRESHOLD));
        var remaining = THRESHOLD - cart.items_subtotal_price;
        if (remaining > 0) {
          meterMsg.textContent = MSG_PROGRESS.replace('[amount]', money(remaining));
          meterMsg.classList.remove('is-unlocked');
        } else {
          meterMsg.textContent = MSG_UNLOCKED;
          meterMsg.classList.add('is-unlocked');
        }
        // fill animates via transform (compositor-only), after the panel settles
        requestAnimationFrame(function () { meterFill.style.transform = 'scaleX(' + (pct / 100) + ')'; });
      }

      itemsEl.innerHTML = cart.items.map(function (item, i) {
        var img = item.image ? '<img src="' + esc(resizeImg(item.image, 160)) + '" width="72" height="72" alt="" loading="lazy">' : '';
        var variant = (item.variant_title && item.variant_title !== 'Default Title')
          ? '<p class="cdrawer__item-variant">' + esc(item.variant_title) + '</p>' : '';
        var plan = item.selling_plan_allocation
          ? '<p class="cdrawer__item-variant">' + esc(item.selling_plan_allocation.selling_plan.name) + '</p>' : '';
        return '<div class="cdrawer__item" style="--i:' + i + '" data-line="' + (i + 1) + '">'
          + '<a href="' + esc(item.url) + '" class="cdrawer__item-media">' + img + '</a>'
          + '<div class="cdrawer__item-body">'
          + '<a class="cdrawer__item-title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>'
          + variant + plan
          + '<div class="cdrawer__item-row">'
          + '<div class="cdrawer__qty">'
          + '<button type="button" data-cd-minus aria-label="Decrease quantity">−</button>'
          + '<span aria-live="polite">' + item.quantity + '</span>'
          + '<button type="button" data-cd-plus aria-label="Increase quantity">+</button>'
          + '</div>'
          + '<span class="cdrawer__item-price">' + money(item.final_line_price) + '</span>'
          + '</div></div>'
          + '<button type="button" class="cdrawer__item-remove" data-cd-remove aria-label="Remove ' + esc(item.product_title) + '">✕</button>'
          + '</div>';
      }).join('');
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      drawerEl.classList.add('is-open');
      drawerEl.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      panel.focus({ preventScroll: true });
    }
    function close() {
      if (!isOpen) return;
      isOpen = false;
      drawerEl.classList.remove('is-open');
      drawerEl.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      // reset the meter so it animates again next open
      meterFill.style.transform = 'scaleX(0)';
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }
    function refresh() {
      return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        render(cart);
        updateBadge(cart.item_count);
        return cart;
      });
    }
    function changeLine(line, qty) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty })
      }).then(function (r) { return r.json(); }).then(function (cart) {
        render(cart);
        updateBadge(cart.item_count);
      });
    }

    drawerEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-cdrawer-close]')) { e.preventDefault(); close(); return; }
      if (e.target.closest('[data-cdrawer-close-link]')) { close(); return; }
      var itemEl = e.target.closest('.cdrawer__item');
      if (!itemEl) return;
      var line = parseInt(itemEl.getAttribute('data-line'), 10);
      var qtyEl = itemEl.querySelector('.cdrawer__qty span');
      var qty = parseInt(qtyEl ? qtyEl.textContent : '1', 10);
      if (e.target.closest('[data-cd-plus]')) changeLine(line, qty + 1);
      else if (e.target.closest('[data-cd-minus]')) changeLine(line, Math.max(0, qty - 1));
      else if (e.target.closest('[data-cd-remove]')) changeLine(line, 0);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close();
    });

    return {
      open: open, close: close, refresh: refresh,
      announce: function (msg) { if (statusEl) statusEl.textContent = msg; },
      openFresh: function () { refresh().then(open); }
    };
  })();

  /* ---- intercept every add-to-cart form ---- */
  if (cdrawer && window.fetch) {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;
      if (form.hasAttribute('data-no-drawer')) return;
      e.preventDefault();

      var btn = form.querySelector('button[type="submit"], [type="submit"]');
      var label = btn ? btn.textContent : '';
      var autoOpen = window.matchMedia('(min-width: 750px)').matches; // drawers hurt mobile CVR
      // optimistic feedback: paint the success state before the network answers
      if (btn) { btn.disabled = true; btn.classList.add('is-added'); btn.textContent = '✓ Added'; }
      if (autoOpen && !document.body.classList.contains('page-cart')) cdrawer.open();

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(form)
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw j; });
        return r.json();
      }).then(function () {
        if (document.body.classList.contains('page-cart')) { window.location.reload(); return; }
        return cdrawer.refresh().then(function (cart) {
          cdrawer.announce('Added to cart. Cart has ' + cart.item_count + ' items.');
          if (btn) {
            setTimeout(function () {
              btn.classList.remove('is-added');
              btn.textContent = label;
              btn.disabled = false;
            }, 1200);
          }
        });
      }).catch(function (err) {
        cdrawer.close();
        if (btn) {
          btn.classList.remove('is-added');
          btn.classList.add('is-error');
          btn.textContent = (err && err.description) ? 'Sold out' : 'Try again';
          setTimeout(function () {
            btn.classList.remove('is-error');
            btn.textContent = label;
            btn.disabled = false;
          }, 1800);
        }
      });
    });

    // header cart icon → drawer (except on the cart page itself)
    document.querySelectorAll('[data-cart-trigger]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        if (document.body.classList.contains('page-cart')) return;
        e.preventDefault();
        cdrawer.openFresh();
      });
    });
  }

  /* =================================================
     PREDICTIVE SEARCH OVERLAY
     ================================================= */
  (function () {
    var sov = document.getElementById('SearchOverlay');
    if (!sov || !window.fetch) return;
    var input = sov.querySelector('[data-sov-input]');
    var results = sov.querySelector('[data-sov-results]');
    var quick = sov.querySelector('[data-sov-quick]');
    var lastFocus = null, isOpen = false, debounceT = null, seq = 0;

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      sov.classList.add('is-open');
      sov.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      input.focus({ preventScroll: true });
    }
    function close() {
      if (!isOpen) return;
      isOpen = false;
      sov.classList.remove('is-open');
      sov.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }

    function renderResults(data, q) {
      var res = data && data.resources && data.resources.results || {};
      var products = res.products || [];
      var pages = (res.pages || []).concat(res.articles || []);
      var html = '';
      if (products.length) {
        html += '<p class="sov__group-label">Products</p><ul class="sov__products" role="list">';
        products.forEach(function (p, i) {
          var img = p.image ? '<img src="' + esc(resizeImg(p.image, 120)) + '" width="52" height="52" alt="" loading="lazy">' : '';
          html += '<li><a class="sov__product" data-sov-item style="--i:' + i + '" href="' + esc(p.url) + '">' + img
            + '<span class="sov__product-title">' + esc(p.title) + '</span>'
            + (p.price ? '<span class="sov__product-price">$' + esc(p.price) + '</span>' : '')
            + '</a></li>';
        });
        html += '</ul>';
      }
      if (pages.length) {
        html += '<p class="sov__group-label">Guides &amp; pages</p><ul class="sov__links" role="list">';
        pages.slice(0, 4).forEach(function (pg) {
          html += '<li><a data-sov-item href="' + esc(pg.url) + '">' + esc(pg.title) + '</a></li>';
        });
        html += '</ul>';
      }
      if (!products.length && !pages.length) {
        html += '<p class="sov__none">No matches for “' + esc(q) + '” — try “saffron threads” or “gift”.</p>';
      }
      html += '<a class="sov__all" data-sov-item href="/search?q=' + encodeURIComponent(q) + '">View all results →</a>';
      results.innerHTML = html;
      activeIdx = -1;
      if (quick) quick.style.display = 'none';
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(debounceT);
      if (q.length < 2) {
        results.innerHTML = '';
        if (quick) quick.style.display = '';
        return;
      }
      debounceT = setTimeout(function () {
        var mySeq = ++seq;
        fetch('/search/suggest.json?q=' + encodeURIComponent(q)
            + '&resources[type]=product,page,article&resources[limit]=4&resources[options][unavailable_products]=last')
          .then(function (r) { return r.json(); })
          .then(function (data) { if (mySeq === seq) renderResults(data, q); })
          .catch(function () { /* network hiccup: leave previous results */ });
      }, 220);
    });

    sov.addEventListener('click', function (e) {
      if (e.target.closest('[data-sov-close]')) { e.preventDefault(); close(); }
    });

    /* keyboard: arrows move (and loop) the highlight, Enter follows it */
    var activeIdx = -1;
    function items() { return results.querySelectorAll('[data-sov-item]'); }
    function highlight(idx) {
      var list = items();
      if (!list.length) return;
      activeIdx = ((idx % list.length) + list.length) % list.length;
      list.forEach(function (el, i) { el.classList.toggle('is-active', i === activeIdx); });
      list[activeIdx].scrollIntoView({ block: 'nearest' });
    }
    document.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight(activeIdx + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(activeIdx - 1); }
      else if (e.key === 'Enter' && activeIdx >= 0) {
        var list = items();
        if (list[activeIdx]) { e.preventDefault(); window.location.href = list[activeIdx].getAttribute('href'); }
      }
    });

    // header search icon → overlay (keep /search as no-JS fallback)
    document.querySelectorAll('a.header__icon-btn[href*="/search"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });
  })();
})();
